import React, { useEffect, useState } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { getData } from "../../../utils/getData";
import { getImageUrl } from "../../../utils/generalLiveCenter";

/**
 * MatchLineup — self-contained football lineup / formation widget.
 *
 * Renders (from /match/{id}/lineup):
 *   • Header: crest + team name + coach + formation, for both sides
 *   • Pitch: starters placed by formation (GK + each formation line), with shirt number,
 *     name, captain, card, and subbed-off minute markers
 *   • Substitutions, Substitutes (bench), and Injured & Suspended lists
 *
 * Data source (priority): serverData prop → hydration DOM (data-listingdata) → self-fetch.
 * Styling is token-driven (src/assets/scss/matchLineup.scss).
 */

// player.cards[] → "r" (red wins) | "y" | null
const cardKind = (cards) => {
  const t = (cards || []).map((c) => String(c?.ty || "").toLowerCase());
  if (t.some((x) => x.includes("red"))) return "r";
  if (t.some((x) => x.includes("yellow"))) return "y";
  return null;
};

const mapStarter = (p) => ({
  sno: p?.sno,
  name: p?.player?.nm || p?.nm || "",
  isCap: p?.is_cap === 1 || p?.is_cap === true,
  card: cardKind(p?.player?.cards),
  subMin: p?.player?.substitutions_out?.minutes || null,
});

// One team's raw lineup object → view model (formation-lined starters + bench/subs/injured).
const buildTeam = (team, coachName, sport) => {
  if (!team) return null;
  const lineup = team.lineup || [];
  const starters = lineup
    .filter((p) => p.isb === 0 || p.match_position === "Starting")
    .sort((a, b) => (a.or || 0) - (b.or || 0));

  const gk = starters.find((p) => /goal/i.test(p.pos)) || starters[0] || null;
  const outfield = starters.filter((p) => p !== gk);

  // Split the 10 outfield players into formation lines, e.g. "4-2-3-1" → [4,2,3,1].
  const digits = String(team.formation || "")
    .split("-")
    .map((n) => parseInt(n, 10))
    .filter((n) => n > 0);
  const lines = [];
  if (digits.length) {
    let i = 0;
    for (const d of digits) {
      lines.push(outfield.slice(i, i + d));
      i += d;
    }
    if (i < outfield.length && lines.length) {
      // any leftover (data/formation mismatch) joins the last line so nobody is dropped
      lines[lines.length - 1] = lines[lines.length - 1].concat(outfield.slice(i));
    }
  } else {
    // no formation string → group by position
    const byPos = (re) => outfield.filter((p) => re.test(p.pos));
    [/(def|back)/i, /mid/i, /(str|forw|att|wing)/i].forEach((re) => {
      const g = byPos(re);
      if (g.length) lines.push(g);
    });
    if (!lines.length && outfield.length) lines.push(outfield);
  }

  return {
    name: team.nm || team.sn || "",
    coach: coachName || "",
    formation: String(team.formation || "").trim(),
    crest: team.id ? getImageUrl("", "", sport, team.id) : null,
    gk: gk ? mapStarter(gk) : null,
    lines: lines.map((line) => line.map(mapStarter)),
    bench: (team.sub_players || []).map((b) => ({ sno: b.sno, name: b.nm })),
    subs: (team.sub || []).map((s) => ({
      min: s.min,
      inName: s.p_in_nm,
      outName: s.p_out_nm,
    })),
    injsusp: (team.inj_susp_players || []).map((x) => ({
      sno: x.sno,
      name: x.nm,
      status: x.status || x.reason || x.type || "Injured",
    })),
  };
};

const lineupFromRaw = (raw) => {
  const d = raw?.data || raw || null; // accept {data:{…}} or the object directly
  if (!d || (!d.home_team && !d.away_team)) return null;
  return d;
};

// ── Small pieces ──────────────────────────────────────────────────────────
const Crest = ({ src, name }) => (
  <span className="ml__crest">
    {src ? <img src={src} alt={name ? `${name} crest` : ""} /> : null}
  </span>
);

// Substitution arrow — green (in) points up, red (out) points down. Inherits colour.
const Arrow = ({ up }) => (
  <svg
    className="ml__arw"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {up ? (
      <>
        <line x1="12" y1="20" x2="12" y2="6" />
        <polyline points="6 12 12 6 18 12" />
      </>
    ) : (
      <>
        <line x1="12" y1="4" x2="12" y2="18" />
        <polyline points="6 12 12 18 18 12" />
      </>
    )}
  </svg>
);

const Player = ({ p }) => (
  <span className="ml__pl" title={`${p.sno ? p.sno + " · " : ""}${p.name}`}>
    <span className="ml__badge">
      {p.sno}
      {p.isCap ? <span className="ml__cap" aria-label="Captain">C</span> : null}
      {p.card ? <span className={`ml__cardmark ml__cardmark--${p.card}`} aria-hidden="true" /> : null}
      {p.subMin ? <span className="ml__off">↓{p.subMin}'</span> : null}
    </span>
    <span className="ml__pname">{p.name}</span>
  </span>
);

const Column = ({ players }) => (
  <span className="ml__col">
    {players.map((p, i) => (
      <Player key={`${p.sno}-${i}`} p={p} />
    ))}
  </span>
);

const Half = ({ team, side }) => (
  <div className={`ml__half ml__half--${side}`}>
    {team.gk ? <Column players={[team.gk]} /> : null}
    {team.lines.map((line, i) => (
      <Column key={i} players={line} />
    ))}
  </div>
);

const TeamHead = ({ team, side }) => (
  <div className={`ml__thead ml__thead--${side}`}>
    <Crest src={team.crest} name={team.name} />
    <div className="ml__tmeta">
      <span className="ml__tname">{team.name}</span>
      <span className="ml__tsub">
        {team.formation ? <span className="ml__formation">{team.formation}</span> : null}
        {team.coach ? <span className="ml__coach">{team.coach}</span> : null}
      </span>
    </div>
  </div>
);

const MatchLineup = ({
  dataId,
  baseUrl,
  sport = "football",
  difference = "+0",
  serverData = null,
  data: dataProp = null,
  dataClient = false,
  loadCSS = true,
}) => {
  useEffect(() => {
    loadStyles("lineupPitch");
  }, []);

  const [raw, setRaw] = useState(lineupFromRaw(serverData || dataProp));

  useEffect(() => {
    const r = lineupFromRaw(serverData || dataProp);
    if (r) setRaw(r);
  }, [serverData, dataProp]);

  // Hydration: re-read the serialized lineup off the container
  useEffect(() => {
    if (!dataClient) return;
    try {
      const el = document.querySelector(".microservice-cont");
      const parsed = JSON.parse(el?.getAttribute("data-listingdata") || "null");
      const r = lineupFromRaw(parsed?.data);
      if (r) setRaw(r);
    } catch (_) {}
  }, [dataClient]);

  // Embed: self-fetch the lineup
  useEffect(() => {
    if (raw || dataClient || !dataId || !baseUrl) return;
    let cancelled = false;
    getData({ dataId, dataType: "match-lineup", baseUrl, sport, difference }).then((res) => {
      if (cancelled) return;
      const r = lineupFromRaw(res);
      if (r) setRaw(r);
    });
    return () => {
      cancelled = true;
    };
  }, [raw, dataClient, dataId, baseUrl, sport, difference]);

  if (!raw) {
    return (
      <div className="ml ml--loading">
        <span>Loading lineups…</span>
      </div>
    );
  }

  const home = buildTeam(raw.home_team, raw.home_team_coach, sport);
  const away = buildTeam(raw.away_team, raw.away_team_coach, sport);
  if (!home && !away) {
    return (
      <div className="ml ml--loading">
        <span>Lineups not available</span>
      </div>
    );
  }

  const teams = [home, away].filter(Boolean);

  return (
    <div className="ml">
      {/* Header */}
      <div className="ml__head">
        {home ? <TeamHead team={home} side="home" /> : <span />}
        <span className="ml__vs">vs</span>
        {away ? <TeamHead team={away} side="away" /> : <span />}
      </div>

      {/* Pitch */}
      <div className="ml__pitch">
        <div className="ml__pitch-lines" aria-hidden="true" />
        {home ? <Half team={home} side="home" /> : null}
        {away ? <Half team={away} side="away" /> : null}
      </div>

      {/* Substitutions made during the match — teams side by side, scrolled */}
      <div className="ml__subs-sec">
        <h4 className="ml__panel-title ml__panel-title--blue">Substitutions</h4>
        <div className="ml__subs-row">
          {teams.map((t, i) => (
            <div className="ml__subs-team" key={i}>
              <span className="ml__panel-team">{t.name}</span>
              {t.subs.length ? (
                <ul className="ml__subs">
                  {t.subs.map((s, j) => (
                    <li key={j} className="ml__sub">
                      <span className="ml__sub-min">{s.min}'</span>
                      <span className="ml__sub-swap">
                        <span className="ml__sub-in"><Arrow up />{s.inName}</span>
                        <span className="ml__sub-out"><Arrow />{s.outName}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="ml__empty">—</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="ml__legend">
        <span className="ml__lg"><i className="ml__lg-cap" aria-hidden="true" /> Captain</span>
        <span className="ml__lg"><i className="ml__lg-card" aria-hidden="true" /> Card</span>
        <span className="ml__lg"><i className="ml__lg-goal" aria-hidden="true" /> Goal</span>
        <span className="ml__lg"><i className="ml__lg-off" aria-hidden="true"><Arrow /></i> Off</span>
      </div>
    </div>
  );
};

export default MatchLineup;
